import { Box, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import { bffApi, type ViewDocumentAsOwnerDto } from "../../../../autogen/bff-api";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import type { StatusVariant } from "../../../../common/components/StatusVariant";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
} from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { useAppDispatch } from "../../../../common/redux/hooks";

// t('Documentation provided') t('Documentation missing') t('Documentation')

interface Props {
  bidId: string;
  minimumRequirementId: string;
  documents: ViewDocumentAsOwnerDto[];
  attachmentIds: string[];
  isReadOnly: boolean;
  onAttachmentsChange: (attachmentIds: string[]) => void;
}

export const MinimumRequirementAttachments = ({
  bidId,
  minimumRequirementId,
  documents: responseDocuments,
  attachmentIds,
  isReadOnly,
  onAttachmentsChange,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const count = attachmentIds.length;
  const variant: StatusVariant = count > 0 ? "ok" : "issue";
  const label = count > 0 ? t("Documentation provided") : t("Documentation missing");

  const reloadEntity = useCallback(() => {
    void dispatch(bffApi.endpoints.getBidV2.initiate({ bidId }, { forceRefetch: true })).unwrap();
  }, [dispatch, bidId]);

  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityType: "BidV2MinimumRequirementAttachment",
    entityId: bidId,
    minimumRequirementId,
    reloadEntity,
  });

  useEffect(() => {
    setDocuments(responseDocuments);
  }, [responseDocuments, setDocuments]);

  const removeDocument = async (document: ViewDocumentAsOwnerDto) => {
    onAttachmentsChange(attachmentIds.filter((id) => id !== document.id));
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="md" p="3" mt="3">
      <Flex mb="2" justify="space-between" align="center" gap="2">
        <HStack spacing="2" align="center">
          <Icon as={FiFileText} color="smMuted" aria-hidden />
          <Text fontSize="sm" fontWeight="semibold">
            {t("Documentation")}
          </Text>
        </HStack>
        <StatusDotLabel variant={variant} label={label} />
      </Flex>
      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        removeUploadedDocument={removeDocument}
        isTagsColumnVisible={false}
        isCategoryColumnVisible={false}
        canDeleteDocuments={!isReadOnly}
        canEditDocumentNames={!isReadOnly}
        documentUploadConfig={
          !isReadOnly
            ? {
                uppy,
                dropTargetId: `minimum-requirement-attachment-${bidId}-${minimumRequirementId}`,
                dropTargetHelperText: t("Drag and drop files here, or click to browse"),
                uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
                removeUpload,
              }
            : undefined
        }
        refetchDocuments={() => {
          reloadEntity();
        }}
      />
    </Box>
  );
};
